import React, { type ReactElement } from 'react';
import BaseMarkerBadge from 'feature-components/badges/marker-badges/base-marker-badge';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { TeacherAuthorisedMark } from 'services/assist-api/entities/test-instance-response-mark/types';
import { MarkType } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useAuthCustomerUser } from 'services/providers/auth.hooks';

type Props = {
  mark: TeacherAuthorisedMark;
  organisationUsers: BaseCustomerUser[];
};

/**
 * A version of the marker badge that can be used in the Teacher-Marking workflow (reports and
 * Manage Marking screen).
 *
 * Under the hood, the markers collection must be loaded as a subobject of a mark, rather than as a
 * seperate request to the test-instance-response-markers endpoint. This is because teachers are not
 * authorised in the API to see other markers of the response who are teachers via the
 * test-instance-response-markers endpoint. Attempting to load the active marker this way would
 * result in a red error badge when teachers view the page without also having a site or marking
 * admin role.
 */
export default function TeacherAuthorisedMarkerBadge({
  mark,
  organisationUsers
}: Props): ReactElement {
  const currentOrganisationUser = useAuthCustomerUser();

  const currentUserMarker = mark.markers?.find((m) => m.user.id === currentOrganisationUser?.id);
  const activeMarker = mark.markers?.find((m) => m.id === mark.activeMarker?.id);
  const activeMarkerOrganisationUser = organisationUsers.find(
    (ou) => ou.id === activeMarker?.user.id
  );

  return (
    <BaseMarkerBadge
      displayWarning={currentUserMarker?.id !== activeMarker?.id}
      activeMarker={activeMarker}
      organisationUser={activeMarkerOrganisationUser}
      autoscored={mark.type === MarkType.Autoscored}
    />
  );
}
